/**
 * Purpose: Utility functions for audio playback and notification sounds
 *
 * How it works:
 * 1. Provides functions to play notification sounds
 * 2. Checks user preferences before playing sounds
 * 3. Handles audio playback errors gracefully
 *
 * Key features:
 * - Sound notification preference management
 * - Error handling for audio playback
 * - Centralized audio functionality
 */

import { safeStorage } from "@/utils/safeStorage";

// Local storage key for sound notification preference
export const SOUND_NOTIFICATION_PREF_KEY = "soundNotificationEnabled";

/**
 * Get the current sound notification preference
 * @returns {boolean} Whether sound notifications are enabled
 */
export function getSoundNotificationPreference(): boolean {
  const pref = safeStorage.get(SOUND_NOTIFICATION_PREF_KEY);
  // Default to true if preference is not set
  return pref === null ? true : pref === "true";
}

/**
 * Set the sound notification preference
 * @param {boolean} enabled - Whether to enable sound notifications
 */
export function setSoundNotificationPreference(enabled: boolean): void {
  safeStorage.set(SOUND_NOTIFICATION_PREF_KEY, String(enabled));
}

/**
 * Play a notification sound if enabled in user preferences
 * @param {string} soundPath - Path to the sound file relative to the public directory, without leading slash (e.g. "sounds/notification-1.mp3")
 * @returns {Promise<void>} Promise that resolves when sound is played or rejected if disabled/error
 */
export function playNotificationSound(
  soundPath: string = "sounds/notification-1.mp3",
): Promise<void> {
  // Check if sound notifications are enabled
  if (!getSoundNotificationPreference()) {
    return Promise.reject(new Error("Sound notifications are disabled"));
  }

  const audio = new Audio(`/${soundPath}`);

  return audio.play().catch((error) => {
    // eslint-disable-next-line no-console
    console.log("Audio playback failed:", error);
    throw error;
  });
}
